import React, { useEffect, useMemo, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  CalendarHeart, Users, ClockAlert, CheckCircle2, XCircle, TrendingUp,
  Search, Filter, LogOut, RefreshCw, Trash2, Phone, Mail, MessageCircle,
  Download, CheckCheck, Hourglass, X as XIcon, Loader2, ChevronDown,
  Eye, MousePointer2, Globe,
} from "lucide-react";
import { api, API_BASE, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { SITE, waLink } from "../lib/site";

const STATUS_META = {
  pendiente:   { label: "Pendiente",   icon: Hourglass,   cls: "bg-amber-50 text-amber-700 ring-amber-200" },
  confirmada:  { label: "Confirmada",  icon: CheckCheck,  cls: "bg-emerald-50 text-emerald-700 ring-emerald-200" },
  completada:  { label: "Completada",  icon: CheckCircle2,cls: "bg-brand-cloud text-brand-medical ring-brand-silver" },
  cancelada:   { label: "Cancelada",   icon: XIcon,       cls: "bg-rose-50 text-rose-700 ring-rose-200" },
};

const STATUS_ORDER = ["pendiente", "confirmada", "completada", "cancelada"];

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || STATUS_META.pendiente;
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ring-1 ${meta.cls}`}>
      <Icon className="w-3 h-3" strokeWidth={2} /> {meta.label}
    </span>
  );
}

function StatusSelect({ value, onChange, testid }) {
  return (
    <div className="relative inline-block">
      <select
        data-testid={testid}
        value={value || "pendiente"}
        onChange={(e) => onChange(e.target.value)}
        className="appearance-none rounded-full ring-1 ring-brand-silver bg-white px-3 py-1.5 pr-8 text-xs font-medium text-brand-deep hover:ring-brand-medical focus:outline-none focus:ring-brand-medical cursor-pointer"
      >
        {STATUS_ORDER.map((s) => (
          <option key={s} value={s}>{STATUS_META[s].label}</option>
        ))}
      </select>
      <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-brand-deep/50 pointer-events-none" />
    </div>
  );
}

function StatCard({ icon: Icon, label, value, hint, accent, testid }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      data-testid={testid}
      className={`relative rounded-3xl p-6 hairline overflow-hidden ${accent ? "bg-gradient-to-br from-brand-deep to-brand-medical text-white" : "bg-white premium-shadow"}`}
    >
      {accent && <div className="absolute -bottom-12 -right-12 w-40 h-40 rounded-full bg-white/10 blur-2xl" />}
      <div className="relative flex items-start justify-between">
        <div>
          <p className={`text-xs uppercase tracking-[0.2em] mb-2 ${accent ? "text-brand-silver" : "text-brand-medical"}`}>{label}</p>
          <p className={`font-serif text-4xl ${accent ? "text-white" : "text-brand-deep"}`}>{value}</p>
          {hint && <p className={`mt-2 text-xs ${accent ? "text-white/70" : "text-brand-deep/55"}`}>{hint}</p>}
        </div>
        <div className={`w-11 h-11 rounded-2xl flex items-center justify-center ${accent ? "bg-white/15 ring-1 ring-white/20" : "bg-brand-cloud ring-1 ring-brand-silver/60"}`}>
          <Icon className={`w-5 h-5 ${accent ? "text-white" : "text-brand-medical"}`} strokeWidth={1.4} />
        </div>
      </div>
    </motion.div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [updating, setUpdating] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, l] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/appointments", {
          params: {
            status: filter === "all" ? undefined : filter,
            q: q.trim() || undefined,
          },
        }),
      ]);
      setStats(s.data);
      setItems(l.data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error al cargar las citas");
    } finally {
      setLoading(false);
    }
  }, [filter, q]);

  useEffect(() => { load(); }, [load]);

  const onStatusChange = async (id, status) => {
    setUpdating(id);
    try {
      const { data } = await api.patch(`/admin/appointments/${id}`, { status });
      setItems((arr) => arr.map((it) => (it.id === id ? { ...it, ...data } : it)));
      toast.success(`Cita ${STATUS_META[status].label.toLowerCase()}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "No se pudo actualizar");
    } finally {
      setUpdating(null);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("¿Eliminar esta cita? Esta acción no se puede deshacer.")) return;
    try {
      await api.delete(`/admin/appointments/${id}`);
      setItems((arr) => arr.filter((it) => it.id !== id));
      toast.success("Cita eliminada");
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "No se pudo eliminar");
    }
  };

  const fmtDate = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleString("es-MX", { dateStyle: "medium", timeStyle: "short" });
    } catch { return iso; }
  };

  const headerStats = useMemo(() => ([
    { i: Users, l: "Total citas", v: stats?.total ?? "—" },
    { i: Hourglass, l: "Pendientes", v: stats?.pendiente ?? "—", accent: true },
    { i: CheckCheck, l: "Confirmadas", v: stats?.confirmada ?? "—" },
    { i: TrendingUp, l: "Últimos 7 días", v: stats?.last_7_days ?? "—" },
  ]), [stats]);

  return (
    <div data-testid="page-admin-dashboard" className="min-h-[80vh]">
      {/* Top bar */}
      <div className="sticky top-24 z-30 mx-auto max-w-7xl px-6 md:px-12 mb-6">
        <div className="glass-strong premium-shadow rounded-3xl p-4 md:p-5 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-3 flex-1 min-w-[200px]">
            <img src={SITE.logo} alt="Logo" className="w-10 h-10 rounded-full ring-1 ring-brand-silver" />
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-brand-medical">CRM · Panel admin</p>
              <p className="font-serif text-lg text-brand-deep leading-tight">
                {user?.name || "Dr. Jairo Franco"}
              </p>
            </div>
          </div>
          <button
            onClick={load}
            data-testid="admin-refresh"
            className="inline-flex items-center gap-2 rounded-full ring-1 ring-brand-silver hover:ring-brand-medical text-brand-medical hover:bg-white px-4 py-2 text-sm font-medium transition-all"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Actualizar
          </button>
          <button
            onClick={logout}
            data-testid="admin-logout"
            className="inline-flex items-center gap-2 rounded-full bg-brand-deep hover:bg-black text-white px-4 py-2 text-sm font-medium transition-all"
          >
            <LogOut className="w-4 h-4" /> Salir
          </button>
        </div>
      </div>

      {/* Stats */}
      <section className="mx-auto max-w-7xl px-6 md:px-12 mb-8">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {headerStats.map((s, i) => (
            <StatCard key={s.l} icon={s.i} label={s.l} value={s.v} accent={s.accent} testid={`stat-${i}`} />
          ))}
        </div>
      </section>

      {/* Visits */}
      <section className="mx-auto max-w-7xl px-6 md:px-12 mb-8" data-testid="visits-section">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Eye className="w-4 h-4 text-brand-medical" strokeWidth={1.5} />
            <p className="text-xs uppercase tracking-[0.2em] text-brand-medical">Visitas al sitio</p>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <StatCard icon={Eye} label="Visitas hoy" value={stats?.visits?.pageviews_today ?? "—"} hint={`${stats?.visits?.unique_visitors_today ?? 0} únicas`} testid="visits-today" />
          <StatCard icon={TrendingUp} label="Últimos 7 días" value={stats?.visits?.pageviews_7d ?? "—"} hint={`${stats?.visits?.unique_visitors_7d ?? 0} únicas`} accent testid="visits-7d" />
          <StatCard icon={MousePointer2} label="Páginas vistas (total)" value={stats?.visits?.pageviews_total ?? "—"} testid="visits-total" />
          <StatCard icon={Users} label="Visitantes únicos" value={stats?.visits?.unique_visitors_total ?? "—"} hint="Histórico" testid="visits-unique" />
        </div>
        {stats?.visits?.top_pages?.length > 0 && (
          <div className="bg-white rounded-3xl hairline premium-shadow p-5" data-testid="top-pages">
            <div className="flex items-center gap-2 mb-4">
              <Globe className="w-4 h-4 text-brand-medical" strokeWidth={1.5} />
              <p className="text-xs uppercase tracking-[0.2em] text-brand-medical">Páginas más vistas (últimos 7 días)</p>
            </div>
            <ul className="space-y-2">
              {stats.visits.top_pages.map((p, i) => {
                const max = stats.visits.top_pages[0]?.views || 1;
                const pct = Math.max(6, Math.round((p.views / max) * 100));
                return (
                  <li key={p.path} className="flex items-center gap-3" data-testid={`top-page-${i}`}>
                    <span className="font-serif text-brand-deep/80 text-sm w-36 truncate">{p.path === "/" ? "Inicio" : p.path}</span>
                    <div className="flex-1 h-2 rounded-full bg-brand-cloud overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-brand-bright to-brand-medical rounded-full transition-all duration-700" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="text-sm font-medium text-brand-deep tabular-nums w-10 text-right">{p.views}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </section>

      {/* Filters */}
      <section className="mx-auto max-w-7xl px-6 md:px-12 mb-5">
        <div className="bg-white rounded-3xl hairline premium-shadow p-4 md:p-5 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-deep/40" />
            <input
              data-testid="admin-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar por nombre, teléfono, motivo..."
              className="w-full rounded-full bg-brand-cloud/60 hairline pl-11 pr-4 py-2.5 text-sm text-brand-deep placeholder:text-brand-deep/40 focus:outline-none focus:ring-2 focus:ring-brand-bright/40"
            />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Filter className="w-4 h-4 text-brand-deep/50" />
            {[
              { k: "all", l: "Todas" },
              ...STATUS_ORDER.map((s) => ({ k: s, l: STATUS_META[s].label })),
            ].map((f) => (
              <button
                key={f.k}
                data-testid={`filter-${f.k}`}
                onClick={() => setFilter(f.k)}
                className={`px-3.5 py-1.5 rounded-full text-xs font-medium ring-1 transition-colors ${
                  filter === f.k
                    ? "bg-brand-medical text-white ring-brand-medical"
                    : "bg-white text-brand-deep/70 ring-brand-silver hover:ring-brand-medical hover:text-brand-medical"
                }`}
              >
                {f.l}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Table */}
      <section className="mx-auto max-w-7xl px-6 md:px-12 mb-16">
        <div className="bg-white rounded-3xl hairline premium-shadow overflow-hidden">
          {loading && items.length === 0 ? (
            <div className="p-12 flex items-center justify-center text-brand-medical">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : items.length === 0 ? (
            <div className="p-16 text-center" data-testid="empty-state">
              <CalendarHeart className="w-10 h-10 text-brand-medical/40 mx-auto mb-3" strokeWidth={1.2} />
              <p className="font-serif text-xl text-brand-deep">Sin citas para mostrar</p>
              <p className="text-sm text-brand-deep/55 mt-1">Cuando reciba citas aparecerán aquí.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="appointments-table">
                <thead>
                  <tr className="bg-brand-cloud/60 text-left">
                    <th className="px-5 py-3 text-xs uppercase tracking-[0.15em] text-brand-medical font-medium">Paciente</th>
                    <th className="px-5 py-3 text-xs uppercase tracking-[0.15em] text-brand-medical font-medium">Contacto</th>
                    <th className="px-5 py-3 text-xs uppercase tracking-[0.15em] text-brand-medical font-medium">Motivo</th>
                    <th className="px-5 py-3 text-xs uppercase tracking-[0.15em] text-brand-medical font-medium">Fecha deseada</th>
                    <th className="px-5 py-3 text-xs uppercase tracking-[0.15em] text-brand-medical font-medium">Recibida</th>
                    <th className="px-5 py-3 text-xs uppercase tracking-[0.15em] text-brand-medical font-medium">Estado</th>
                    <th className="px-5 py-3 text-xs uppercase tracking-[0.15em] text-brand-medical font-medium text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-brand-cloud">
                  {items.map((a, idx) => (
                    <motion.tr
                      key={a.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: Math.min(idx * 0.02, 0.3) }}
                      data-testid={`appointment-row-${a.id}`}
                      className="hover:bg-brand-snow transition-colors"
                    >
                      <td className="px-5 py-4 align-top">
                        <div className="font-serif text-base text-brand-deep">{a.nombre}</div>
                        {a.email && <div className="text-xs text-brand-deep/55 mt-0.5">{a.email}</div>}
                      </td>
                      <td className="px-5 py-4 align-top whitespace-nowrap">
                        <div className="flex flex-col gap-1.5">
                          <a href={`tel:+${a.telefono.replace(/\D/g, "")}`} className="inline-flex items-center gap-1.5 text-brand-deep hover:text-brand-medical">
                            <Phone className="w-3.5 h-3.5" strokeWidth={1.5} /> {a.telefono}
                          </a>
                          <a
                            href={waLink(`Hola ${a.nombre}, te escribo del consultorio del Dr. Jairo Franco para confirmar tu cita.`)}
                            target="_blank" rel="noopener noreferrer"
                            data-testid={`wa-${a.id}`}
                            className="inline-flex items-center gap-1.5 text-emerald-700 hover:underline text-xs"
                          >
                            <MessageCircle className="w-3.5 h-3.5" strokeWidth={1.5} /> WhatsApp
                          </a>
                        </div>
                      </td>
                      <td className="px-5 py-4 align-top max-w-[260px]">
                        <p className="text-brand-deep/85 font-light leading-snug line-clamp-3">{a.motivo}</p>
                      </td>
                      <td className="px-5 py-4 align-top text-brand-deep/75 whitespace-nowrap">
                        {a.fecha_deseada || <span className="text-brand-deep/40">—</span>}
                      </td>
                      <td className="px-5 py-4 align-top text-brand-deep/55 whitespace-nowrap text-xs">
                        {fmtDate(a.created_at)}
                      </td>
                      <td className="px-5 py-4 align-top">
                        <div className="flex flex-col gap-2">
                          <StatusBadge status={a.status} />
                          {updating === a.id ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin text-brand-medical" />
                          ) : (
                            <StatusSelect
                              value={a.status}
                              onChange={(s) => onStatusChange(a.id, s)}
                              testid={`status-select-${a.id}`}
                            />
                          )}
                        </div>
                      </td>
                      <td className="px-5 py-4 align-top">
                        <div className="flex items-center justify-end gap-2">
                          <a
                            href={`${API_BASE}/appointments/${a.id}/ics`}
                            data-testid={`download-ics-${a.id}`}
                            className="inline-flex items-center gap-1 rounded-full ring-1 ring-brand-silver hover:ring-brand-medical text-brand-medical hover:bg-brand-cloud/40 px-3 py-1.5 text-xs font-medium transition-colors"
                            title="Descargar evento (.ics) para Google Calendar"
                          >
                            <Download className="w-3.5 h-3.5" /> .ics
                          </a>
                          <button
                            onClick={() => onDelete(a.id)}
                            data-testid={`delete-${a.id}`}
                            className="inline-flex items-center gap-1 rounded-full ring-1 ring-rose-200 hover:ring-rose-400 text-rose-600 hover:bg-rose-50 px-3 py-1.5 text-xs font-medium transition-colors"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
